<!-- The treasurer's own cards: cycle, due date, limit, and available credit from what's logged. -->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import {
    cardPurchases,
    cardStatus,
    creditTotals,
    formatMicro,
    balanceSeries,
    monthsBetween,
    trendDates,
    MONEY_SYSTEM,
    moneyPrompt,
    parseMoneyAdvice,
    rewardsFor,
    REWARD_CATEGORIES,
    REWARDS_SYSTEM,
    parseRewardsReply,
    knownCard,
    KNOWN_CARDS,
    NETWORKS,
    networkFromIssuer,
    cardKindLine,
    cardLabel,
    capUse,
    closeDaysFrom,
    welcomeProgress,
    AMEX_CLOSE_DAYS,
    type CapRule,
    type CardDetails,
    type MoneyProgram,
    type RewardCategory,
    type CardRewards,
    type MoneyAdvice,
    type MoneyEntry,
    type CardPurchase,
    type GroupState,
    type MoneyAccount,
  } from "@split/core";
  import LineChart from "../components/charts/LineChart.svelte";
  import ColumnChart from "../components/charts/ColumnChart.svelte";
  import { assistant } from "../lib/ai/assistant.svelte.ts";
  import { chat, LlmError } from "../lib/ai/llm.ts";
  import { aiQueue } from "../lib/ai/queue.svelte.ts";
  import { api } from "../lib/api.ts";
  import { groupPath } from "../lib/routes.ts";
  import { link } from "../lib/router.svelte.ts";
  import { app } from "../lib/store.svelte.ts";
  import { moneyCards } from "../lib/moneyCards.svelte.ts";
  import { centsInput, readMoney, shortDate, today } from "../lib/format.ts";

  let accounts = $state<MoneyAccount[]>([]);
  let entries = $state<MoneyEntry[]>([]);
  let programs = $state<MoneyProgram[]>([]);
  let groups = $state<{ id: string; name: string; state: GroupState }[]>([]);
  let error = $state("");
  let loaded = $state(false);
  let editing = $state<string | null>(null);
  let open = $state<string | null>(null);
  let showArchived = $state(false);

  const day = $derived(today());

  async function load() {
    try {
      const r = await api<{ accounts: MoneyAccount[]; entries?: MoneyEntry[]; programs?: MoneyProgram[] }>("/api/admin/money");
      accounts = r.accounts;
      entries = r.entries ?? [];
      programs = r.programs ?? [];
      error = "";
    } catch (e) {
      error = (e as Error).message;
    }
    // Purchases come from the groups this device has opened (all of them for the treasurer, usually).
    const out: typeof groups = [];
    for (const g of app.groups) {
      const state = await app.groupSnapshot(g.id);
      if (state) out.push({ id: g.id, name: state.name || g.name, state });
    }
    groups = out;
    loaded = true;
  }
  $effect(() => {
    void app.groups.length;
    void app.othersVersion; // another group got new changes
    void load();
  });

  const rows = $derived(
    accounts
      .filter((a) => a.kind === "credit" && (showArchived || !a.archived))
      .map((account) => {
        const buys = cardPurchases(account, groups, day);
        return { account, buys, status: cardStatus(account, buys, day) };
      }),
  );
  const totals = $derived(creditTotals(rows));
  const personal = $derived(rows.filter((x) => !x.account.details?.business));
  const business = $derived(rows.filter((x) => x.account.details?.business));
  const sections = $derived(
    business.length
      ? [
          { title: "Personal", list: personal, t: creditTotals(personal) },
          { title: "Business", list: business, t: creditTotals(business) },
        ].filter((x) => x.list.length)
      : [{ title: "", list: personal, t: totals }],
  );
  const states = $derived(groups.map((g) => g.state));
  const archivedCount = $derived(accounts.filter((a) => a.archived).length);

  /** Card names used in the ledgers that no card claims yet, to offer when adding one. */
  const unclaimed = $derived.by(() => {
    const claimed = new Set(accounts.flatMap((a) => [a.name, ...a.matchNames]).map((n) => n.trim().toLowerCase()));
    const names = new Set<string>();
    for (const g of groups) {
      for (const c of g.state.settings.cards) if (!claimed.has(c.trim().toLowerCase())) names.add(c);
      for (const it of Object.values(g.state.items)) {
        const p = it.paidWith?.trim();
        if (p && !claimed.has(p.toLowerCase())) names.add(p);
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  });

  // ---- the add / edit form
  const blank = () => ({
    kind: "credit" as MoneyAccount["kind"],
    name: "",
    last4: "",
    issuer: "",
    limit: "",
    balance: "",
    balanceOn: day,
    statementDay: "",
    dueDay: "",
    matchNames: "",
    note: "",
    rUnit: "points" as CardRewards["unit"],
    rPoint: "",
    rates: {} as Record<string, string>,
    caps: [] as CapForm[],
    network: "" as "" | NonNullable<CardDetails["network"]>,
    tier: "",
    charge: false,
    business: false,
    cycleMode: "monthly" as "monthly" | "before-due",
    closeDays: String(AMEX_CLOSE_DAYS),
    lastClose: "",
    programId: "",
    wOn: false,
    wSpend: "",
    wFrom: day,
    wBy: "",
    wReward: "",
    wOffset: "",
    wReceived: false,
  });
  interface CapForm {
    label: string;
    pairs: { cat: RewardCategory; rate: string }[];
    cap: string;
    period: CapRule["period"];
    from: string;
    until: string;
    after: string;
  }
  const blankCap = (): CapForm => ({ label: "", pairs: [{ cat: "online", rate: "" }], cap: "2500", period: "quarter", from: "", until: "", after: "" });
  let f = $state(blank());
  let saving = $state(false);

  function edit(a: MoneyAccount) {
    editing = a.id;
    f = {
      kind: a.kind,
      note: a.note ?? "",
      rUnit: a.rewards?.unit ?? "points",
      rPoint: a.rewards?.pointCents ? String(a.rewards.pointCents) : "",
      rates: Object.fromEntries(Object.entries(a.rewards?.rates ?? {}).map(([k, v]) => [k, String(v)])),
      caps: (a.rewards?.caps ?? []).map((c) => ({
        label: c.label ?? "",
        pairs: Object.entries(c.rates).map(([cat, rate]) => ({ cat: cat as RewardCategory, rate: String(rate) })),
        cap: centsInput(c.capCents),
        period: c.period,
        from: c.from ?? "",
        until: c.until ?? "",
        after: c.after === undefined ? "" : String(c.after),
      })),
      network: a.details?.network ?? "",
      tier: a.details?.tier ?? "",
      charge: !!a.details?.charge,
      business: !!a.details?.business,
      cycleMode: a.details?.cycle?.mode ?? "monthly",
      closeDays: a.details?.cycle?.mode === "before-due" ? String(a.details.cycle.days) : String(AMEX_CLOSE_DAYS),
      lastClose: "",
      programId: a.details?.programId ?? "",
      wOn: !!a.details?.welcome,
      wSpend: a.details?.welcome ? centsInput(a.details.welcome.spendCents) : "",
      wFrom: a.details?.welcome?.from ?? day,
      wBy: a.details?.welcome?.by ?? "",
      wReward: a.details?.welcome ? String(a.details.welcome.unit === "cash" ? a.details.welcome.reward / 100 : a.details.welcome.reward) : "",
      wOffset: a.details?.welcome?.offsetCents ? centsInput(a.details.welcome.offsetCents) : "",
      wReceived: !!a.details?.welcome?.received,
      name: a.name,
      last4: a.last4,
      issuer: a.issuer,
      limit: a.limitCents === null ? "" : centsInput(a.limitCents),
      balance: centsInput(a.balanceCents),
      balanceOn: a.balanceOn,
      statementDay: a.statementDay ? String(a.statementDay) : "",
      dueDay: a.dueDay ? String(a.dueDay) : "",
      matchNames: a.matchNames.join(", "),
    };
  }

  function startNew(name = "", kind: MoneyAccount["kind"] = "credit") {
    editing = "new";
    f = { ...blank(), kind, name, matchNames: kind === "credit" ? name : "" };
  }

  /** Typing a card we know fills in its network, tier, charge/business, cycle and earn rates. */
  function autofill() {
    if (f.kind !== "credit") return;
    const k = knownCard(f.name, f.issuer);
    if (!k) {
      if (!f.network) f.network = networkFromIssuer(f.issuer) ?? "";
      return;
    }
    if (!f.issuer) f.issuer = k.issuer;
    if (!f.network && k.details.network) f.network = k.details.network;
    if (!f.tier && k.details.tier) f.tier = k.details.tier;
    if (k.details.charge) f.charge = true;
    if (k.details.business) f.business = true;
    if (k.details.network === "amex" && editing === "new") f.cycleMode = "before-due";
    const hasRates = Object.values(f.rates).some((v) => v.trim());
    if (k.rewards && !hasRates && !f.caps.length) {
      f.rUnit = k.rewards.unit;
      f.rPoint = k.rewards.pointCents ? String(k.rewards.pointCents) : "";
      f.rates = Object.fromEntries(Object.entries(k.rewards.rates).map(([c, v]) => [c, String(v)]));
      f.caps = (k.rewards.caps ?? []).map((c) => ({
        label: c.label ?? "",
        pairs: Object.entries(c.rates).map(([cat, rate]) => ({ cat: cat as RewardCategory, rate: String(rate) })),
        cap: centsInput(c.capCents),
        period: c.period,
        from: c.from ?? "",
        until: c.until ?? "",
        after: c.after === undefined ? "" : String(c.after),
      }));
    }
    if (k.details.network === "amex" && !f.programId && f.rUnit === "points") {
      const mr = programs.find((p) => /membership rewards/i.test(p.name));
      if (mr) f.programId = mr.id;
    }
  }
  /** From a statement close you saw, work out how many days before the due date it closes. */
  const calibrated = $derived.by(() => {
    const due = Number(f.dueDay);
    if (!f.lastClose || !(due >= 1 && due <= 31)) return null;
    return closeDaysFrom(f.lastClose, due);
  });
  $effect(() => {
    if (calibrated !== null && f.cycleMode === "before-due") f.closeDays = String(calibrated);
  });
  const tierOptions = $derived(NETWORKS.find((n) => n.id === f.network)?.tiers ?? []);

  const dayNum = (t: string) => {
    if (!t.trim()) return null;
    const n = Number(t);
    return Number.isInteger(n) && n >= 1 && n <= 31 ? n : NaN;
  };

  async function save(e: SubmitEvent) {
    e.preventDefault();
    const limit = f.limit.trim() ? readMoney(f.limit) : null;
    const balance = f.balance.trim() ? readMoney(f.balance) : 0;
    const statementDay = dayNum(f.statementDay);
    const dueDay = dayNum(f.dueDay);
    if (f.limit.trim() && limit === null) return app.toast("Enter the limit like 5000", "error");
    if (balance === null) return app.toast("Enter the balance like 123.45", "error");
    if (Number.isNaN(statementDay) || Number.isNaN(dueDay)) return app.toast("Days are 1 to 31", "error");
    const details = readDetails();
    if (typeof details === "string") return app.toast(details, "error");
    const rewards = readRewards();
    if (typeof rewards === "string") return app.toast(rewards, "error");
    const body = {
      name: f.name.trim(),
      last4: f.last4.trim(),
      issuer: f.issuer.trim(),
      limitCents: f.charge ? null : limit,
      balanceCents: balance,
      balanceOn: f.balanceOn || day,
      statementDay,
      dueDay,
      matchNames: [...new Set(f.matchNames.split(",").map((s) => s.trim()).filter(Boolean))],
      note: f.note.trim(),
      rewards,
      details,
    };
    saving = true;
    try {
      if (editing === "new") await api("/api/admin/money", { body: { kind: f.kind, ...body } });
      else await api(`/api/admin/money/${editing}`, { method: "PATCH", body });
      app.toast("Saved");
      editing = null;
      await load();
      moneyCards.refresh(true);
    } catch (err) {
      app.toast((err as Error).message, "error");
    } finally {
      saving = false;
    }
  }

  /** The rates you typed, or null when none are filled in. */
  function readRewards(): CardRewards | null | string {
    if (f.kind !== "credit") return null;
    const rates: CardRewards["rates"] = {};
    for (const { id } of REWARD_CATEGORIES) {
      const v = Number((f.rates[id] ?? "").trim());
      if ((f.rates[id] ?? "").trim() !== "" && Number.isFinite(v) && v >= 0) rates[id] = v;
    }
    const caps: CapRule[] = [];
    for (const c of f.caps) {
      const r: CapRule["rates"] = {};
      for (const p of c.pairs) {
        const v = Number(p.rate);
        if (p.rate.trim() && Number.isFinite(v) && v >= 0) r[p.cat] = v;
      }
      if (!Object.keys(r).length) continue;
      const capCents = readMoney(c.cap || "0");
      if (capCents === null) return "Enter each cap like 2500";
      const after = c.after.trim() ? Number(c.after) : undefined;
      caps.push({
        ...(c.label.trim() ? { label: c.label.trim() } : {}),
        rates: r,
        capCents,
        period: c.period,
        ...(c.from ? { from: c.from } : {}),
        ...(c.until ? { until: c.until } : {}),
        ...(after !== undefined && Number.isFinite(after) ? { after } : {}),
      });
    }
    if (!Object.keys(rates).length && !caps.length) return null;
    if (rates.other === undefined) rates.other = 1;
    const pc = Number(f.rPoint);
    return {
      unit: f.rUnit,
      ...(f.rUnit === "points" && pc > 0 ? { pointCents: pc } : {}),
      rates,
      ...(caps.length ? { caps } : {}),
      source: "you",
    };
  }

  function readDetails(): CardDetails | null | string {
    if (f.kind !== "credit") return null;
    const days = Math.round(Number(f.closeDays));
    if (f.cycleMode === "before-due" && !(days >= 10 && days <= 40)) return "Days before the due date are 10 to 40";
    if (f.cycleMode === "before-due" && !f.dueDay.trim()) return "Add the payment due day so the close date can be worked out";
    let welcome: CardDetails["welcome"] = null;
    if (f.wOn) {
      const spend = readMoney(f.wSpend);
      const offset = f.wOffset.trim() ? readMoney(f.wOffset) : 0;
      const reward = Number(f.wReward);
      if (!spend || !f.wBy || !(reward > 0) || offset === null) return "Fill in the welcome bonus: spend, by when, and the reward";
      welcome = {
        spendCents: spend,
        from: f.wFrom || day,
        by: f.wBy,
        reward: f.rUnit === "cash" ? Math.round(reward * 100) : Math.round(reward),
        unit: f.rUnit,
        ...(offset ? { offsetCents: offset } : {}),
        ...(f.wReceived ? { received: true } : {}),
      };
    }
    return {
      ...(f.network ? { network: f.network } : {}),
      ...(f.tier.trim() ? { tier: f.tier.trim() } : {}),
      ...(f.charge ? { charge: true } : {}),
      ...(f.business ? { business: true } : {}),
      cycle: f.cycleMode === "before-due" ? { mode: "before-due", days } : { mode: "monthly" },
      welcome,
      programId: f.programId || null,
    };
  }

  // ---- points programs (Amex Membership Rewards, Rakuten…): balances you type in
  let progForm = $state<{ id: string | null; name: string; unit: "points" | "cash"; posted: string; pending: string; pointCents: string; paysTo: string; note: string } | null>(null);
  function editProgram(p: MoneyProgram | null) {
    progForm = p
      ? { id: p.id, name: p.name, unit: p.unit, posted: String(p.unit === "cash" ? p.posted / 100 : p.posted), pending: String(p.unit === "cash" ? p.pending / 100 : p.pending), pointCents: String(p.pointCents), paysTo: p.paysTo ?? "", note: p.note }
      : { id: null, name: "", unit: "points", posted: "0", pending: "0", pointCents: "1", paysTo: "", note: "" };
  }
  async function saveProgram(e: SubmitEvent) {
    e.preventDefault();
    if (!progForm) return;
    const n = (t: string) => (progForm!.unit === "cash" ? Math.round(Number(t || 0) * 100) : Math.round(Number(t || 0)));
    const body = {
      name: progForm.name.trim(),
      unit: progForm.unit,
      posted: n(progForm.posted),
      pending: n(progForm.pending),
      asOf: day,
      pointCents: Number(progForm.pointCents) || 1,
      paysTo: progForm.paysTo || null,
      note: progForm.note.trim(),
    };
    if (!Number.isFinite(body.posted) || !Number.isFinite(body.pending)) return app.toast("Enter balances as numbers", "error");
    try {
      if (progForm.id) await api(`/api/admin/money/programs/${progForm.id}`, { method: "PATCH", body });
      else await api("/api/admin/money/programs", { body });
      progForm = null;
      app.toast("Saved");
      await load();
    } catch (err) {
      app.toast((err as Error).message, "error");
    }
  }
  async function removeProgram(id: string) {
    await api(`/api/admin/money/programs/${id}`, { method: "DELETE" });
    progForm = null;
    await load();
  }
  const programName = (id: string | null | undefined) => programs.find((p) => p.id === id)?.name ?? "";
  const fmtPts = (p: MoneyProgram, v: number) => (p.unit === "cash" ? formatMicro(v * 10_000) : `${v.toLocaleString()} pts`);

  // Cards with no earn rates: the AI suggests them in the background (you can change them).
  aiQueue.register({
    id: "card-rewards",
    label: "Cards",
    progress: () => (rewardsTotal ? [rewardsDone, rewardsTotal] : null),
    due: () => loaded && accounts.some((a) => a.kind === "credit" && !a.archived && !a.rewards && !rewardsTried.has(a.id)),
    // Every card still missing rates, one after another: "AI: Cards (4/16)".
    run: async (signal) => {
      const todo = accounts.filter((x) => x.kind === "credit" && !x.archived && !x.rewards && !rewardsTried.has(x.id));
      if (!todo.length) return;
      if (assistant.connection.status !== "ok") await assistant.check();
      if (assistant.connection.status !== "ok") return;
      rewardsTotal = todo.length;
      rewardsDone = 0;
      try {
        for (const a of todo) {
          if (signal.aborted) return;
          const res = await chat(
            assistant.settings,
            [
              { role: "system", content: REWARDS_SYSTEM },
              { role: "user", content: `Card: ${a.name}${a.issuer ? ` (${a.issuer})` : ""}${a.note ? `. Notes: ${a.note}` : ""}` },
            ],
            [],
            () => {},
            signal,
          );
          rewardsTried.add(a.id); // asked once this visit, even when the model didn't know it
          const rewards = parseRewardsReply(res.content);
          if (rewards) await api(`/api/admin/money/${a.id}`, { method: "PATCH", body: { rewards } });
          rewardsDone++;
        }
      } finally {
        rewardsTotal = 0;
        await load();
      }
    },
  });
  let rewardsDone = $state(0);
  let rewardsTotal = $state(0);
  const rewardsTried = new Set<string>();

  async function quickBalance(a: MoneyAccount, text: string) {
    const cents = readMoney(text);
    if (cents === null) return app.toast("Enter the balance like 123.45", "error");
    try {
      await api(`/api/admin/money/${a.id}/entries`, { body: { kind: "balance", on: day, cents } });
      app.toast("Balance updated");
      await load();
    } catch (err) {
      app.toast((err as Error).message, "error");
    }
  }

  /** A payment to a card: kept for the history chart, and the balance drops by it. */
  async function logPayment(a: MoneyAccount, owedMicro: number, text: string) {
    const cents = readMoney(text);
    if (!cents || cents < 0) return app.toast("Enter the payment like 250.00", "error");
    try {
      await api(`/api/admin/money/${a.id}/entries`, { body: { kind: "payment", on: day, cents } });
      await api(`/api/admin/money/${a.id}/entries`, {
        body: { kind: "balance", on: day, cents: Math.round(owedMicro / 10_000) - cents },
      });
      app.toast("Payment logged");
      await load();
    } catch (err) {
      app.toast((err as Error).message, "error");
    }
  }

  // ---- bank accounts (entered by hand for now; bank sync can fill them later)
  const banks = $derived(accounts.filter((a) => a.kind !== "credit" && (showArchived || !a.archived)));
  const cash = $derived(banks.filter((b) => !b.archived).reduce((s, b) => s + b.balanceCents, 0));

  // ---- charts per card: Compact or Graph, remembered per card on this device
  function readView(id: string): "compact" | "graph" {
    try {
      return localStorage.getItem(`money:view:${id}`) === "graph" ? "graph" : "compact";
    } catch {
      return "compact";
    }
  }
  let views = $state<Record<string, "compact" | "graph">>({});
  const viewOf = (id: string) => views[id] ?? readView(id);
  function setView(id: string, v: "compact" | "graph") {
    views[id] = v;
    try {
      localStorage.setItem(`money:view:${id}`, v);
    } catch {
      /* fine */
    }
  }
  function balanceChart(a: MoneyAccount, buys: CardPurchase[]) {
    const d = new Date(`${day}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 90);
    const dates = trendDates(d.toISOString().slice(0, 10), day);
    return balanceSeries(a, entries, buys, dates).map((p) => ({ label: shortDate(p.date), value: p.valueMicro }));
  }
  const months6 = $derived.by(() => {
    const d = new Date(`${day}T12:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() - 5);
    return monthsBetween(d.toISOString().slice(0, 7), day.slice(0, 7));
  });
  function paymentsChart(a: MoneyAccount) {
    const pays = entries.filter((e) => e.accountId === a.id && e.kind === "payment");
    return [
      {
        name: "Payments",
        color: "var(--series-3)",
        values: months6.map((m) => pays.filter((p) => p.on.startsWith(m)).reduce((s, p) => s + p.cents, 0) * 10_000),
      },
    ];
  }

  // ---- AI suggestions: on demand, or by itself daily or weekly while the app is open
  type Saved = { at: string; items: MoneyAdvice[] };
  function readSaved(): Saved | null {
    try {
      return JSON.parse(localStorage.getItem("money:ai:last") ?? "null") as Saved | null;
    } catch {
      return null;
    }
  }
  let advice = $state<Saved | null>(readSaved());
  let advising = $state(false);
  let adviceError = $state("");
  let every = $state<"off" | "daily" | "weekly">(
    (() => {
      try {
        const v = localStorage.getItem("money:ai:every");
        return v === "daily" || v === "weekly" ? v : "off";
      } catch {
        return "off";
      }
    })(),
  );
  function setEvery(v: "off" | "daily" | "weekly") {
    every = v;
    try {
      localStorage.setItem("money:ai:every", v);
    } catch {
      /* fine */
    }
  }
  function suggest(): Promise<void> {
    return aiQueue.first((id) => suggestNow(undefined, id), "Money");
  }
  async function suggestNow(signal?: AbortSignal, job = "") {
    if (advising) return;
    advising = true;
    adviceError = "";
    try {
      if (assistant.connection.status !== "ok") await assistant.check();
      if (assistant.connection.status !== "ok") throw new LlmError(assistant.connection.message || "Can't reach LM Studio. Set it up on the Ask page.", "unreachable");
      const all = [
        ...rows.map((x) => ({ account: x.account, status: x.status })),
        ...banks.map((b) => ({ account: b })),
      ];
      const r = await chat(
        assistant.settings,
        [
          { role: "system", content: MONEY_SYSTEM },
          { role: "user", content: moneyPrompt(all, day) },
        ],
        [],
        (u) => aiQueue.streamed(job, u.content.length + u.reasoning.length, 1500),
        signal,
      );
      const items = parseMoneyAdvice(r.content);
      if (!items.length) throw new Error("The model didn't return suggestions. Try again, or try another model.");
      advice = { at: new Date().toISOString(), items };
      try {
        localStorage.setItem("money:ai:last", JSON.stringify(advice));
      } catch {
        /* fine */
      }
    } catch (e) {
      adviceError = (e as Error).message;
    } finally {
      advising = false;
    }
  }
  // Daily or weekly suggestions run as background AI work (when you're idle or overnight).
  aiQueue.register({
    id: "money-suggestions",
    label: "Money",
    due: () => {
      if (every === "off" || !loaded || !accounts.length || advising) return false;
      const age = advice ? Date.now() - Date.parse(advice.at) : Infinity;
      return age > (every === "daily" ? 1 : 7) * 86_400_000;
    },
    run: (signal) => suggestNow(signal),
  });

  async function setArchived(a: MoneyAccount, archived: boolean) {
    await api(`/api/admin/money/${a.id}`, { method: "PATCH", body: { archived } });
    await load();
  }

  async function remove(a: MoneyAccount) {
    await api(`/api/admin/money/${a.id}`, { method: "DELETE" });
    editing = null;
    await load();
  }

  const pct = (bp: number | null) => (bp === null ? "" : `${Math.round(bp / 100)}%`);
  const level = (bp: number | null) => (bp === null ? "" : bp >= 5000 ? "high" : bp >= 3000 ? "mid" : "low");
  const when = (d: number | null) => (d === null ? "" : d === 0 ? "today" : d === 1 ? "tomorrow" : `in ${d} days`);
  const total = (list: CardPurchase[]) => list.reduce((s, p) => s + p.totalMicro, 0);
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>Money</h1>
      <p class="lede">Your own cards and bank accounts. Only you see this page. Enter balances now and then; purchases you log on a card in your groups are added, so you know what's left before the statement closes.</p>
    </div>
    {#if editing === null}
      <div class="btns">
        <button class="btn primary" onclick={() => startNew()}>Add a card</button>
        <button class="btn" onclick={() => startNew("", "checking")}>Add a bank account</button>
      </div>
    {/if}
  </div>

  {#if error}<p class="owes">{error}</p>{/if}

  {#if rows.length}
    <div class="totals">
      <div><span class="muted">Total limit</span><strong>{formatMicro(totals.limitMicro)}</strong></div>
      <div><span class="muted">Owed</span><strong>{formatMicro(totals.owedMicro)}</strong></div>
      <div><span class="muted">Available</span><strong class="ok">{formatMicro(totals.availableMicro)}</strong></div>
      <div><span class="muted">Used</span><strong class={level(totals.utilizationBp)}>{pct(totals.utilizationBp) || "—"}</strong></div>
    </div>
  {/if}

  {#if editing !== null}
    <form class="card-form" onsubmit={save}>
      <h2>{editing === "new" ? (f.kind === "credit" ? "Add a card" : "Add a bank account") : `Edit ${f.name}`}</h2>
      <div class="grid">
        <label class="field"><span>Name</span><input class="input" bind:value={f.name} placeholder={f.kind === "credit" ? "Amex Gold" : "BofA checking"} list={f.kind === "credit" ? "known-cards" : undefined} onchange={autofill} required /></label>
        <datalist id="known-cards">{#each KNOWN_CARDS as k (k.name)}<option value={k.name}>{k.issuer}</option>{/each}</datalist>
        {#if f.kind !== "credit"}
          <label class="field"
            ><span>Type</span>
            <select class="input" bind:value={f.kind}>
              <option value="checking">Checking</option>
              <option value="savings">Savings or brokerage</option>
            </select></label
          >
        {/if}
        <label class="field"><span>Last 4</span><input class="input" bind:value={f.last4} inputmode="numeric" maxlength="4" placeholder="1005" /></label>
        <label class="field"><span>{f.kind === "credit" ? "Issuer" : "Bank or brokerage"}</span><input class="input" bind:value={f.issuer} placeholder={f.kind === "credit" ? "Amex" : "Fidelity"} /></label>
        {#if f.kind === "credit"}
          <label class="field"
            ><span>Network</span>
            <select class="input" bind:value={f.network}>
              <option value="">Not set</option>
              {#each NETWORKS as n (n.id)}<option value={n.id}>{n.label}</option>{/each}
            </select></label
          >
          {#if tierOptions.length}
            <label class="field"
              ><span>Tier</span>
              <select class="input" bind:value={f.tier}>
                <option value="">Not set</option>
                {#each tierOptions as t (t)}<option value={t}>{t}</option>{/each}
              </select></label
            >
          {/if}
          {#if !f.charge}
            <label class="field"><span>Credit limit</span><input class="input money" bind:value={f.limit} inputmode="decimal" placeholder="5000" /></label>
          {/if}
        {/if}
        <label class="field"><span>Balance now</span><input class="input money" bind:value={f.balance} inputmode="decimal" placeholder="0.00" /></label>
        <label class="field"><span>Balance as of</span><input class="input" type="date" bind:value={f.balanceOn} /></label>
        {#if f.kind === "credit"}
          <label class="field"><span>Payment due on day</span><input class="input" bind:value={f.dueDay} inputmode="numeric" placeholder="10" /></label>
          <label class="field"
            ><span>Statement closes</span>
            <select class="input" bind:value={f.cycleMode}>
              <option value="monthly">The same day each month</option>
              <option value="before-due">Days before the due date (Amex)</option>
            </select></label
          >
          {#if f.cycleMode === "monthly"}
            <label class="field"><span>Statement closes on day</span><input class="input" bind:value={f.statementDay} inputmode="numeric" placeholder="15" /></label>
          {:else}
            <label class="field"><span>Days before the due date</span><input class="input" bind:value={f.closeDays} inputmode="numeric" placeholder="25" /></label>
            <label class="field"><span>Last statement closed on (to check)</span><input class="input" type="date" bind:value={f.lastClose} /></label>
          {/if}
        {/if}
      </div>
      {#if f.kind === "credit"}
        <div class="checks">
          <label class="check"><input type="checkbox" bind:checked={f.charge} /> Charge card (no preset spending limit, like Amex Green or Gold)</label>
          <label class="check"><input type="checkbox" bind:checked={f.business} /> Business card</label>
        </div>
        {#if f.cycleMode === "before-due" && f.lastClose}
          <p class="muted small">
            {calibrated === null ? "That close date doesn't line up with the due day; check both." : `It closes ${calibrated} days before the due date, so the next statements are worked out from that.`}
          </p>
        {/if}
      {/if}
      {#if f.kind === "credit"}
        <fieldset class="rewards">
          <legend>Rewards</legend>
          <div class="grid">
            <label class="field"
              ><span>Earns</span>
              <select class="input" bind:value={f.rUnit}>
                <option value="points">Points (per dollar)</option>
                <option value="cash">Cashback (percent)</option>
              </select></label
            >
            {#if f.rUnit === "points"}
              <label class="field"><span>A point is worth (¢)</span><input class="input" bind:value={f.rPoint} inputmode="decimal" placeholder="2" /></label>
            {/if}
            {#each REWARD_CATEGORIES as c (c.id)}
              <label class="field"
                ><span>{c.label}</span><input class="input" inputmode="decimal" placeholder={c.id === "other" ? "1" : ""} value={f.rates[c.id] ?? ""} oninput={(e) => (f.rates = { ...f.rates, [c.id]: (e.currentTarget as HTMLInputElement).value })} /></label
              >
            {/each}
          </div>
          <h3 class="sub">Capped or limited-time rates</h3>
          {#each f.caps as c, ci (ci)}
            <div class="cap">
              <label class="field wide"><span>What it is</span><input class="input" bind:value={c.label} placeholder="First year: 6% online, first $2,500 a quarter" /></label>
              {#each c.pairs as pr, pi (pi)}
                <div class="pair">
                  <select class="input" bind:value={pr.cat} aria-label="Category">
                    {#each REWARD_CATEGORIES as rc (rc.id)}<option value={rc.id}>{rc.label}</option>{/each}
                  </select>
                  <input class="input" bind:value={pr.rate} inputmode="decimal" placeholder={f.rUnit === "cash" ? "% back" : "pts/$"} aria-label="Rate" />
                  {#if c.pairs.length > 1}<button type="button" class="btn small quiet" onclick={() => c.pairs.splice(pi, 1)}>Remove</button>{/if}
                </div>
              {/each}
              <button type="button" class="link-btn" onclick={() => c.pairs.push({ cat: "grocery", rate: "" })}>Add a category to this cap</button>
              <div class="grid">
                <label class="field"><span>Up to ($ spent)</span><input class="input money" bind:value={c.cap} inputmode="decimal" /></label>
                <label class="field"
                  ><span>Each</span>
                  <select class="input" bind:value={c.period}><option value="month">Month</option><option value="quarter">Quarter</option><option value="year">Year</option></select></label
                >
                <label class="field"><span>Then ({f.rUnit === "cash" ? "%" : "pts/$"})</span><input class="input" bind:value={c.after} inputmode="decimal" placeholder={f.rates.other || "1"} /></label>
                <label class="field"><span>Starts</span><input class="input" type="date" bind:value={c.from} /></label>
                <label class="field"><span>Ends (for intro offers)</span><input class="input" type="date" bind:value={c.until} /></label>
              </div>
              <button type="button" class="btn small quiet" onclick={() => f.caps.splice(ci, 1)}>Remove this cap</button>
            </div>
          {/each}
          <button type="button" class="btn small" onclick={() => f.caps.push(blankCap())}>Add a capped rate</button>
          <div class="grid">
            <label class="field"
              ><span>Points go to</span>
              <select class="input" bind:value={f.programId}>
                <option value="">The card itself</option>
                {#each programs as p (p.id)}<option value={p.id}>{p.name}</option>{/each}
              </select></label
            >
          </div>
          {#if H.MONEY_RATES_NOTE.trim()}<p class="muted small">{H.MONEY_RATES_NOTE}</p>{/if}
        </fieldset>
        <fieldset class="rewards">
          <legend><label class="check"><input type="checkbox" bind:checked={f.wOn} /> Welcome bonus</label></legend>
          {#if f.wOn}
            <div class="grid">
              <label class="field"><span>Spend</span><input class="input money" bind:value={f.wSpend} inputmode="decimal" placeholder="3000" /></label>
              <label class="field"><span>From</span><input class="input" type="date" bind:value={f.wFrom} /></label>
              <label class="field"><span>By</span><input class="input" type="date" bind:value={f.wBy} /></label>
              <label class="field"><span>To get ({f.rUnit === "cash" ? "$" : "points"})</span><input class="input" bind:value={f.wReward} inputmode="decimal" placeholder={f.rUnit === "cash" ? "200" : "60000"} /></label>
              <label class="field"><span>Already spent, not logged here</span><input class="input money" bind:value={f.wOffset} inputmode="decimal" placeholder="0" /></label>
            </div>
            <label class="check"><input type="checkbox" bind:checked={f.wReceived} /> Received it</label>
          {/if}
        </fieldset>
      {/if}
      <label class="field">
        <span>Notes (the AI suggestions read these)</span>
        <input class="input" bind:value={f.note} placeholder={f.kind === "credit" ? "4x at restaurants, annual fee in March" : "Cash management account, core position FDLXX; debit card linked"} />
      </label>
      {#if f.kind === "credit"}
      <label class="field">
        <span>Names it goes by in the Card column (comma separated)</span>
        <input class="input" bind:value={f.matchNames} placeholder="Amex, Amex Gold" />
      </label>
      {#if H.MONEY_CARDS_NOTE.trim()}<p class="muted small">{H.MONEY_CARDS_NOTE}</p>{/if}
      {/if}
      {#if f.kind === "credit" && unclaimed.length}
        <p class="muted small">
          Used in your ledgers:
          {#each unclaimed as n (n)}<button type="button" class="chip" onclick={() => (f.matchNames = [...f.matchNames.split(",").map((s) => s.trim()).filter(Boolean), n].join(", "))}>{n}</button>{/each}
        </p>
      {/if}
      <div class="btns">
        <button class="btn primary" disabled={saving || !f.name.trim()}>Save</button>
        <button type="button" class="btn quiet" onclick={() => (editing = null)}>Cancel</button>
        {#if editing !== "new"}
          {@const a = accounts.find((x) => x.id === editing)}
          {#if a}
            <button type="button" class="btn quiet" onclick={() => setArchived(a, !a.archived).then(() => (editing = null))}>{a.archived ? "Bring back" : "Archive"}</button>
            <button type="button" class="btn quiet danger" onclick={() => remove(a)}>Remove</button>
          {/if}
        {/if}
      </div>
    </form>
  {/if}

  {#each sections as sec (sec.title)}
    {#if sec.title}
      <div class="sec-head cards-head">
        <h2>{sec.title}</h2>
        <span class="muted small">Owed {formatMicro(sec.t.owedMicro)}{sec.t.limitMicro ? ` · ${formatMicro(sec.t.availableMicro)} available · ${pct(sec.t.utilizationBp)} used` : ""}</span>
      </div>
    {/if}
    <div class="cards">
      {#each sec.list as { account: a, status: s, buys } (a.id)}
      <article class="card" class:archived={a.archived}>
        <header>
          <div>
            <strong>{a.name}</strong>
            <span class="muted small">{[a.issuer, a.last4 ? `•••• ${a.last4}` : ""].filter(Boolean).join(" ")}</span>
            {#if cardKindLine(a.details, a.issuer)}<span class="kind">{cardKindLine(a.details, a.issuer)}</span>{/if}
          </div>
          <span class="hdr-actions">
            <span class="seg" role="group" aria-label="Show {a.name} as">
              <button aria-pressed={viewOf(a.id) === "compact"} onclick={() => setView(a.id, "compact")}>Compact</button>
              <button aria-pressed={viewOf(a.id) === "graph"} onclick={() => setView(a.id, "graph")}>Graph</button>
            </span>
            <button class="btn small quiet" onclick={() => edit(a)}>Edit</button>
          </span>
        </header>
        {#if viewOf(a.id) === "graph"}
          <LineChart title="Balance, last 90 days" points={balanceChart(a, rows.find((x) => x.account.id === a.id)?.buys ?? [])} height={150} />
          <ColumnChart title="Payments by month" labels={months6.map((m) => m.slice(5))} series={paymentsChart(a)} height={120} />
        {/if}
        {#if a.limitCents !== null && !a.details?.charge}
          <div class="bar" role="img" aria-label="{pct(s.utilizationBp)} of the limit used">
            <span class={level(s.utilizationBp)} style="width: {Math.min(100, (s.utilizationBp ?? 0) / 100)}%"></span>
          </div>
        {/if}
        <dl>
          {#if a.details?.charge}
            <div><dt>Limit</dt><dd>No preset limit</dd></div>
          {:else}
            <div><dt>Available</dt><dd class="ok">{s.availableMicro === null ? "—" : formatMicro(s.availableMicro)}</dd></div>
          {/if}
          <div><dt>Owed</dt><dd>{formatMicro(s.owedMicro)}{#if s.utilizationBp !== null} <span class="muted small">({pct(s.utilizationBp)})</span>{/if}</dd></div>
          <div><dt>This cycle</dt><dd>{formatMicro(s.cycleMicro)}</dd></div>
          {#if s.nextClose}<div><dt>Statement closes</dt><dd>{shortDate(s.nextClose)} <span class="muted small">{when(s.daysToClose)}</span></dd></div>{/if}
          {#if s.nextDue}<div><dt>Payment due</dt><dd class:soon={(s.daysToDue ?? 99) <= 5}>{shortDate(s.nextDue)} <span class="muted small">{when(s.daysToDue)}</span></dd></div>{/if}
        </dl>
        {#if a.rewards}
          {@const rw = rewardsFor(a, buys, states, { from: s.lastClose })}
          {#if rw}
            <p class="earned">
              Earning this cycle: <strong>{rw.unit === "points" ? `${rw.amount.toLocaleString()} points` : `${formatMicro(rw.amount * 10_000)} back`}</strong>
              {#if rw.unit === "points"}<span class="muted">(about {formatMicro(rw.valueCents * 10_000)})</span>{/if}
              <span class="muted small">posts after the statement closes</span>
              {#if a.rewards.source === "ai"}<span class="ai-tag" title="Rates suggested by the AI; edit the card to change them">AI rates</span>{/if}
            </p>
          {/if}
          {#each capUse(a.rewards, buys, states, day) as u, ui (ui)}
            <div class="meter">
              <span class="small">{u.cap.label || "Capped rate"}: {formatMicro(u.usedCents * 10_000)} of {formatMicro(u.cap.capCents * 10_000)} this {u.cap.period}{u.cap.until ? ` · until ${shortDate(u.cap.until)}` : ""}</span>
              <span class="track"><span style:width="{Math.min(100, (u.usedCents / Math.max(1, u.cap.capCents)) * 100)}%"></span></span>
            </div>
          {/each}
        {/if}
        {#if a.details?.programId}
          {@const p = programs.find((x) => x.id === a.details?.programId)}
          {#if p}
            <p class="earned small">{p.name}: <strong>{fmtPts(p, p.posted)}</strong> posted · <strong>{fmtPts(p, p.pending)}</strong> pending <span class="muted">(as of {shortDate(p.asOf)})</span></p>
          {/if}
        {/if}
        {#if a.details?.welcome}
          {@const w = a.details.welcome}
          {@const wp = welcomeProgress(w, buys, day)}
          <div class="meter welcome" class:done={wp.done}>
            <span class="small">
              Welcome bonus, {w.unit === "cash" ? formatMicro(w.reward * 10_000) : `${w.reward.toLocaleString()} points`}:
              {#if wp.done}earned{w.received ? " and received" : ", posts soon"}{:else if wp.expired}ended {shortDate(w.by)} with {formatMicro(wp.leftCents * 10_000)} to go{:else}{formatMicro(wp.spentCents * 10_000)} of {formatMicro(w.spendCents * 10_000)} · {formatMicro(wp.leftCents * 10_000)} to go by {shortDate(w.by)} ({wp.daysLeft} days){/if}
            </span>
            <span class="track"><span style:width="{wp.pct}%"></span></span>
          </div>
        {/if}
        <p class="muted small">
          {formatMicro(a.balanceCents * 10_000)} on {shortDate(a.balanceOn)}{#if s.loggedSince.length}, plus {s.loggedSince.length} {s.loggedSince.length === 1 ? "purchase" : "purchases"} logged since ({formatMicro(total(s.loggedSince))}){/if}.
          {#if !a.matchNames.length}Add the names it goes by in your ledgers to count purchases.{/if}
        </p>
        <div class="quick">
          <input
            class="input money"
            inputmode="decimal"
            placeholder="Balance today"
            aria-label="Balance today for {a.name}"
            onkeydown={(e) => {
              if (e.key === "Enter") void quickBalance(a, (e.currentTarget as HTMLInputElement).value);
            }}
          />
          <input
            class="input money"
            inputmode="decimal"
            placeholder="Paid today"
            aria-label="Payment today for {a.name}"
            onkeydown={(e) => {
              if (e.key === "Enter") void logPayment(a, s.owedMicro, (e.currentTarget as HTMLInputElement).value);
            }}
          />
          {#if s.loggedSince.length}
            <button class="btn small quiet" onclick={() => (open = open === a.id ? null : a.id)}>{open === a.id ? "Hide" : "Show"} purchases</button>
          {/if}
        </div>
        {#if open === a.id}
          <ul class="buys">
            {#each s.loggedSince as p (p.groupId + p.itemId)}
              <li><span>{shortDate(p.date)} · {p.store} · {p.name} <span class="muted small">({p.groupName})</span></span><span class="num">{formatMicro(p.totalMicro)}</span></li>
            {/each}
          </ul>
        {/if}
      </article>
      {/each}
    </div>
  {/each}
  {#if !rows.length && loaded && editing === null}
    <div class="cards">
      <div class="empty">
        <p>No cards yet. Add one with its limit, statement day and due day.</p>
        {#if unclaimed.length}
          <p class="muted small">Cards in your ledgers: {#each unclaimed as n (n)}<button type="button" class="chip" onclick={() => startNew(n)}>{n}</button>{/each}</p>
        {/if}
      </div>
    </div>
  {/if}

  <section class="banks">
    <div class="sec-head">
      <h2>Points and cashback</h2>
      {#if !progForm}<button class="btn small" onclick={() => editProgram(null)}>Add a program</button>{/if}
    </div>
    {#if progForm}
      <form class="card-form" onsubmit={saveProgram}>
        <div class="grid">
          <label class="field"><span>Name</span><input class="input" bind:value={progForm.name} placeholder="Amex Membership Rewards, Rakuten" list="program-names" required /></label>
          <datalist id="program-names"><option value="Amex Membership Rewards"></option><option value="Chase Ultimate Rewards"></option><option value="Capital One Miles"></option><option value="Rakuten"></option></datalist>
          <label class="field"
            ><span>Earns</span>
            <select class="input" bind:value={progForm.unit}><option value="points">Points</option><option value="cash">Cash</option></select></label
          >
          <label class="field"><span>Posted</span><input class="input" bind:value={progForm.posted} inputmode="decimal" /></label>
          <label class="field"><span>Pending</span><input class="input" bind:value={progForm.pending} inputmode="decimal" /></label>
          {#if progForm.unit === "points"}<label class="field"><span>A point is worth (¢)</span><input class="input" bind:value={progForm.pointCents} inputmode="decimal" /></label>{/if}
          <label class="field"
            ><span>Pays out to</span>
            <select class="input" bind:value={progForm.paysTo}>
              <option value="">Itself</option>
              {#each programs.filter((p) => p.id !== progForm?.id) as p (p.id)}<option value={p.id}>{p.name}</option>{/each}
            </select></label
          >
        </div>
        <label class="field"><span>Notes</span><input class="input" bind:value={progForm.note} placeholder="Rakuten pays quarterly as MR points" /></label>
        <div class="btns">
          <button class="btn primary" disabled={!progForm.name.trim()}>Save</button>
          <button type="button" class="btn quiet" onclick={() => (progForm = null)}>Cancel</button>
          {#if progForm.id}<button type="button" class="btn quiet danger" onclick={() => progForm?.id && removeProgram(progForm.id)}>Remove</button>{/if}
        </div>
      </form>
    {/if}
    {#each programs as p (p.id)}
      {@const feeds = programs.filter((x) => x.paysTo === p.id)}
      <div class="bank">
        <span class="who">
          <strong>{p.name}</strong>
          <span class="muted small">{[`as of ${shortDate(p.asOf)}`, p.paysTo ? `pays out to ${programName(p.paysTo)}` : "", feeds.length ? `${feeds.map((x) => `${x.name} adds ${fmtPts(x, x.pending)} pending`).join(", ")}` : "", p.note].filter(Boolean).join(" · ")}</span>
        </span>
        <span class="num">{fmtPts(p, p.posted)} <span class="muted small">posted</span> · {fmtPts(p, p.pending)} <span class="muted small">pending</span>{#if p.unit === "points"}<span class="muted small"> ≈ {formatMicro(Math.round((p.posted + p.pending) * p.pointCents) * 10_000)}</span>{/if}</span>
        <span></span>
        <button class="btn small quiet" onclick={() => editProgram(p)}>Update</button>
      </div>
    {:else}
      {#if !progForm}{#if H.MONEY_PROGRAMS_NOTE.trim()}<p class="muted small">{H.MONEY_PROGRAMS_NOTE}</p>{/if}{/if}
    {/each}
  </section>
  <section class="banks">
    <div class="sec-head">
      <h2>Bank accounts</h2>
      {#if banks.length}<span class="muted">{formatMicro(cash * 10_000)} across {banks.length} {banks.length === 1 ? "account" : "accounts"}</span>{/if}
    </div>
    {#each banks as b (b.id)}
      <div class="bank" class:archived={b.archived}>
        <span class="who">
          <strong>{b.name}</strong>
          <span class="muted small">{[b.kind === "savings" ? "Savings or brokerage" : "Checking", b.issuer, b.last4 ? `•••• ${b.last4}` : ""].filter(Boolean).join(" · ")}{b.note ? ` · ${b.note}` : ""}</span>
        </span>
        <span class="num">{formatMicro(b.balanceCents * 10_000)}<span class="muted small"> on {shortDate(b.balanceOn)}</span></span>
        <input
          class="input money"
          inputmode="decimal"
          placeholder="Balance today"
          aria-label="Balance today for {b.name}"
          onkeydown={(e) => {
            if (e.key === "Enter") void quickBalance(b, (e.currentTarget as HTMLInputElement).value);
          }}
        />
        <button class="btn small quiet" onclick={() => edit(b)}>Edit</button>
      </div>
    {:else}
      {#if H.MONEY_ACCOUNTS_NOTE.trim()}<p class="muted small">{H.MONEY_ACCOUNTS_NOTE}</p>{/if}
    {/each}
  </section>

  <section class="advice">
    <div class="sec-head">
      <h2>Suggestions</h2>
      <span class="adv-actions">
        <label class="small"
          >Check by itself
          <select class="input mini-select" value={every} onchange={(e) => setEvery((e.currentTarget as HTMLSelectElement).value as "off" | "daily" | "weekly")}>
            <option value="off">Off</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select></label
        >
        <button class="btn small primary" disabled={advising || !accounts.length} onclick={suggest}>{advising ? "Thinking…" : "Suggest moves"}</button>
      </span>
    </div>
    {#if H.MONEY_ADVICE_NOTE.trim()}<p class="muted small">{H.MONEY_ADVICE_NOTE}</p>{/if}
    {#if adviceError}<p class="owes small">{adviceError}</p>{/if}
    {#if advice}
      <ul class="adv">
        {#each advice.items as it, i (i)}
          <li class={it.urgency}>
            <span class="tag">{it.urgency === "now" ? "Now" : it.urgency === "soon" ? "Soon" : "Idea"}</span>
            <span><strong>{it.title}</strong>{#if it.why}<br /><span class="muted">{it.why}</span>{/if}</span>
          </li>
        {/each}
      </ul>
      <p class="muted small">From {shortDate(advice.at.slice(0, 10))}.</p>
    {/if}
  </section>

  {#if archivedCount}
    <label class="small muted arch"><input type="checkbox" bind:checked={showArchived} /> Show {archivedCount} archived</label>
  {/if}
  {#if H.MONEY_FOOT_NOTE.trim()}<p class="muted small foot">{H.MONEY_FOOT_NOTE}</p>{/if}
</div>

<style>
  .rewards {
    margin: 0;
    padding: 10px 12px;
    border: 1px solid var(--rule);
    border-radius: var(--r-md);
  }

  .checks {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
  }

  .sub {
    margin: 12px 0 6px;
    font-size: var(--t-sm);
  }

  .cap {
    display: grid;
    gap: 8px;
    padding: 10px;
    margin-bottom: 8px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .pair {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 100px auto;
    gap: 6px;
  }

  .kind {
    display: block;
    color: var(--ink-2);
    font-size: var(--t-xs);
    font-weight: 600;
  }

  .meter {
    display: grid;
    gap: 4px;
  }

  .meter .track {
    height: 6px;
    border-radius: 999px;
    background: var(--wash);
    overflow: hidden;
  }

  .meter .track span {
    display: block;
    height: 100%;
    border-radius: 999px;
    background: var(--series-1);
  }

  .meter.welcome .track span {
    background: var(--series-4);
  }

  .meter.done .track span {
    background: var(--ok);
  }

  .cards-head {
    margin-top: 18px;
  }

  .rewards legend {
    padding: 0 4px;
    font-weight: 650;
  }

  .earned {
    margin: 0;
    font-size: var(--t-sm);
  }

  .ai-tag {
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 999px;
    background: color-mix(in srgb, #8b5cd6 16%, transparent);
    color: #8b5cd6;
    font-size: 0.6875rem;
    font-weight: 700;
  }

  .hdr-actions {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .seg {
    display: inline-flex;
    padding: 2px;
    border-radius: 8px;
    background: var(--wash);
  }

  .seg button {
    padding: 3px 8px;
    border: 0;
    border-radius: 6px;
    background: none;
    font: inherit;
    font-size: var(--t-xs);
    color: var(--muted);
    cursor: pointer;
  }

  .seg button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    font-weight: 650;
  }

  .banks,
  .advice {
    margin-top: 24px;
  }

  .sec-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  .sec-head h2 {
    margin: 0;
    font-size: var(--t-lg);
  }

  .bank {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto 140px auto;
    gap: 12px;
    align-items: center;
    padding: 12px 16px;
    margin-bottom: 6px;
    border-radius: var(--r-lg);
    background: var(--surface);
  }

  .bank .who {
    display: grid;
    min-width: 0;
  }

  .bank.archived {
    opacity: 0.6;
  }

  @media (max-width: 640px) {
    .bank {
      grid-template-columns: 1fr auto;
    }
  }

  .adv-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .mini-select {
    width: auto;
    min-height: 30px;
    padding: 2px 8px;
    margin-left: 4px;
  }

  .adv {
    list-style: none;
    margin: 8px 0;
    padding: 0;
    display: grid;
    gap: 6px;
  }

  .adv li {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    padding: 12px 14px;
    border-radius: var(--r-lg);
    background: var(--surface);
  }

  .adv .tag {
    flex: none;
    padding: 1px 8px;
    border-radius: 999px;
    background: var(--wash);
    font-size: var(--t-xs);
    font-weight: 650;
  }

  .adv li.now .tag {
    background: var(--owes-wash);
    color: var(--owes);
  }

  .glink {
    margin-right: 8px;
  }

  .small {
    font-size: var(--t-xs);
  }
  .totals {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
    margin-bottom: 18px;
  }
  .totals > div {
    display: grid;
    gap: 2px;
    padding: 12px 14px;
    border-radius: var(--r-lg);
    background: var(--surface);
  }
  .totals strong {
    font-size: 1.2rem;
  }
  .ok {
    color: var(--ok);
  }
  .high,
  .soon {
    color: var(--owes);
  }
  .mid {
    color: #b7860b;
  }
  .card-form {
    display: grid;
    gap: 12px;
    padding: 16px;
    margin-bottom: 18px;
    border-radius: var(--r-lg);
    background: var(--surface);
  }
  .card-form h2 {
    margin: 0;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
  }
  .btns {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .chip {
    margin: 2px 4px 2px 0;
    padding: 2px 10px;
    border-radius: 999px;
    border: 1px solid var(--line, #ccc);
    background: var(--surface);
    font-size: var(--t-xs);
    cursor: pointer;
  }
  .cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 14px;
  }
  .card {
    display: grid;
    gap: 10px;
    align-content: start;
    padding: 16px;
    border-radius: var(--r-lg);
    background: var(--surface);
  }
  .card.archived {
    opacity: 0.6;
  }
  .card header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    gap: 8px;
  }
  .card header div {
    display: grid;
  }
  .bar {
    height: 8px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--muted) 18%, transparent);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--ok);
  }
  .bar span.mid {
    background: #d9a514;
  }
  .bar span.high {
    background: var(--owes);
  }
  dl {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 12px;
    margin: 0;
  }
  dt {
    font-size: var(--t-xs);
    color: var(--muted);
  }
  dd {
    margin: 0;
    font-weight: 600;
  }
  .card p {
    margin: 0;
  }
  .quick {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .quick .input {
    max-width: 150px;
  }
  .buys {
    margin: 0;
    padding: 0;
    list-style: none;
    display: grid;
    gap: 4px;
    font-size: var(--t-xs);
  }
  .buys li {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }
  .arch {
    display: inline-flex;
    gap: 6px;
    margin-top: 12px;
  }
  .foot {
    margin-top: 18px;
  }
</style>
